import React from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { Van, Repeat, Factory, Stack } from "@phosphor-icons/react";
import { Card, Empty, Stat, aud, num } from "./ui";

export function SystemChart({ rows }) {
  return (
    <Card title="Warranty cost by system" icon={Stack} testId="wi-system-chart">
      {!rows.length ? <Empty>No data.</Empty> : (
        <div style={{ height: Math.max(200, rows.length * 26) }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rows} layout="vertical" margin={{ left: 10 }}>
              <XAxis type="number" stroke="#525252" fontSize={11} tickFormatter={(v) => `$${Math.round(v / 1000)}k`} />
              <YAxis type="category" dataKey="system" stroke="#737373" fontSize={11} width={150} />
              <Tooltip contentStyle={{ background: "#0F0F0F", border: "1px solid #262626", fontSize: 12 }} formatter={(v, k) => (k === "cost" ? aud(v) : v)} />
              <Bar dataKey="cost" fill="#d9b45b" radius={[0, 4, 4, 0]} name="cost" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}

export function ModelTable({ rows }) {
  return (
    <Card title="Models with the highest reported faults" icon={Van} testId="wi-model-table">
      <table className="w-full text-xs">
        <thead className="text-[0.6rem] uppercase tracking-wider text-neutral-500 text-left"><tr className="border-b border-neutral-800">
          <th className="py-2 pr-3">Model</th><th className="py-2 pr-3 text-right">Faults</th><th className="py-2 pr-3 text-right">Caravans</th>
          <th className="py-2 pr-3 text-right">Per 100 in fleet</th><th className="py-2 pr-3 text-right">Cost</th><th className="py-2">Top components</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.model} className="border-b border-neutral-900 align-top">
              <td className="py-2 pr-3 text-neutral-100">{r.model}</td><td className="py-2 pr-3 text-right tabular-nums">{num(r.faults)}</td>
              <td className="py-2 pr-3 text-right tabular-nums">{num(r.caravans)}</td>
              <td className="py-2 pr-3 text-right tabular-nums text-neutral-400">{r.per_100 ?? "—"}{r.fleet ? <span className="text-neutral-600"> of {num(r.fleet)}</span> : ""}</td>
              <td className="py-2 pr-3 text-right tabular-nums text-[#d9b45b]">{aud(r.cost)}</td><td className="py-2 text-neutral-400">{r.top_components.join(", ")}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

export function ProductionYears({ rows, note }) {
  return (
    <Card title="Claim rate by production year" icon={Factory} testId="wi-production-years">
      <p className="text-[0.7rem] text-neutral-500 mb-3">{note}</p>
      {!rows.length ? <Empty>No production years identified.</Empty> : (
        <table className="w-full text-xs">
          <thead className="text-[0.6rem] uppercase tracking-wider text-neutral-500 text-left"><tr className="border-b border-neutral-800">
            <th className="py-2 pr-3">Year</th><th className="py-2 pr-3 text-right">Faults</th><th className="py-2 pr-3 text-right">Caravans with claims</th><th className="py-2 pr-3 text-right">Built</th><th className="py-2 text-right">Per 100</th></tr></thead>
          <tbody>{rows.map((r) => (
            <tr key={r.year} className="border-b border-neutral-900"><td className="py-2 pr-3 text-neutral-100">{r.year}</td><td className="py-2 pr-3 text-right tabular-nums">{num(r.faults)}</td>
              <td className="py-2 pr-3 text-right tabular-nums">{num(r.caravans)}</td><td className="py-2 pr-3 text-right tabular-nums text-neutral-500">{num(r.built)}</td>
              <td className="py-2 text-right tabular-nums text-amber-300">{r.per_100 ?? "—"}</td></tr>
          ))}</tbody>
        </table>
      )}
    </Card>
  );
}

export function RepeatCard({ r }) {
  return (
    <Card title="Repeat repairs" icon={Repeat} testId="wi-repeat-card">
      <div className="grid grid-cols-3 gap-2">
        <Stat label="Repeat rate" value={`${r.repeat_rate_pct}%`} sub="of affected caravans" tone="amber" />
        <Stat label="Caravans" value={num(r.caravans_with_repeat)} sub="same component twice+" />
        <Stat label="After rectification" value={num(r.after_rectification)} sub="failed again after an approved repair" tone="red" />
      </div>
      {r.top.length > 0 && <ul className="mt-3 text-xs text-neutral-400 space-y-1">{r.top.map((t) => <li key={`${t.system}-${t.component}`}><span className="text-neutral-200">{t.component}</span> · {t.faults} repeat reports</li>)}</ul>}
    </Card>
  );
}
